import { CongaLanguageOption, CongaPresigningSigningUrlResult, CongaPresigningState } from 'types';

import { api } from './api';

interface TokenArgs {
  inviteToken: string;
}

interface LanguageArgs extends TokenArgs {
  language: CongaLanguageOption;
  otherLanguage?: string;
}

const TAG = { type: 'ContractSigning' as const, id: 'STATE' };

const post = (path: string, inviteToken: string, body?: object) => ({
  url: `/contract-signing/${path}`,
  method: 'POST',
  params: { inviteToken },
  ...(body && { body }),
});

export const contractSigningApi = api.injectEndpoints({
  endpoints: (build) => ({
    getContractSigningState: build.query<CongaPresigningState, TokenArgs>({
      query: ({ inviteToken }) => ({ url: '/contract-signing/state', params: { inviteToken } }),
      providesTags: [TAG],
    }),
    chooseContractLanguage: build.mutation<{ outcome: 'set' | 'notOffered' }, LanguageArgs>({
      query: ({ inviteToken, ...body }) => post('language', inviteToken, body),
      invalidatesTags: [TAG],
    }),
    recordLanguagePreference: build.mutation<void, LanguageArgs>({
      query: ({ inviteToken, ...body }) => post('preference', inviteToken, body),
      invalidatesTags: [TAG],
    }),
    acknowledgeEnglishOnlyNotice: build.mutation<void, TokenArgs>({
      query: ({ inviteToken }) => post('english-only-notice', inviteToken),
      invalidatesTags: [TAG],
    }),
    acknowledgeKeyTerms: build.mutation<void, TokenArgs>({
      query: ({ inviteToken }) => post('key-terms', inviteToken),
      invalidatesTags: [TAG],
    }),
    giveSigningConsent: build.mutation<void, TokenArgs & { disclosureIds: string[] }>({
      query: ({ inviteToken, disclosureIds }) => post('consent', inviteToken, { disclosureIds }),
      invalidatesTags: [TAG],
    }),
    getSigningUrl: build.mutation<CongaPresigningSigningUrlResult, TokenArgs>({
      query: ({ inviteToken }) => post('signing-url', inviteToken),
    }),
  }),
});

export const {
  useGetContractSigningStateQuery,
  useChooseContractLanguageMutation,
  useRecordLanguagePreferenceMutation,
  useAcknowledgeEnglishOnlyNoticeMutation,
  useAcknowledgeKeyTermsMutation,
  useGiveSigningConsentMutation,
  useGetSigningUrlMutation,
} = contractSigningApi;
